import { useTableDisplayState } from "@/components/DataTable";
import type { SortOrder } from "@/features/it-operation-core";
import type { operations } from "@/lib/api/schema";
import { usePaginationSearch, useSortingSearch } from "@/lib/listPage";
import { useNavigate } from "@tanstack/react-router";
import {
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import {
  type TransformedCertificationsTableRow,
  useCertificationsColumns,
} from "../-columns";
import { useCertificationsQuery } from "../-query";
import { OrderBy } from "../-zod-schema";
import { useCertificationsSearch } from "./useCertificationsSearch";

export const useCertificationsTable = (personId?: string) => {
  const { search } = useCertificationsSearch();
  const navigate = useNavigate();

  const [pagination, setPagination] = usePaginationSearch({
    search: {
      ...search,
      pageIndex: Math.max(0, (search.page || 1) - 1),
      pageSize: search.pageSize ?? 10,
    },
    onSearchChange: (paginationSearch) => {
      const updatedSearch = {
        ...paginationSearch,
        page: Math.max(1, (paginationSearch.pageIndex || 0) + 1),
      };
      navigate({ to: ".", search: { ...search, ...updatedSearch } });
    },
  });

  const convertToApiColumn = (
    column: string | undefined,
  ): NonNullable<
    operations["CertificationController_getCertifications_v2"]["parameters"]["query"]
  >["orderBy"] => {
    if (!column) return undefined;
    switch (column) {
      case "certificationId":
        return OrderBy.CERTIFICATION_ID;
      case "certificationName":
        return OrderBy.CERTIFICATION_NAME;
      case "certificationParty":
        return OrderBy.CERTIFICATION_PARTY;
      case "effectiveDate":
        return OrderBy.EFFECTIVE_DATE;
      case "expirationDate":
        return OrderBy.EXPIRATION_DATE;
      case "renewalDate":
        return OrderBy.RENEWAL_DATE;
      case "certificationHolder":
        return OrderBy.CERTIFICATION_HOLDER;
      default:
        return undefined;
    }
  };

  const [sorting, setSorting] = useSortingSearch({
    search,
    onSearchChange: (sortingSearch: Record<string, unknown>) =>
      navigate({ to: ".", search: { ...search, ...sortingSearch } }),
  });

  const columns = useCertificationsColumns();

  const apiParams: NonNullable<
    operations["CertificationController_getCertifications_v2"]["parameters"]["query"]
  > = {
    personId: personId,
    freeText: search.freeText,
    page: search.page || 1,
    pageSize: search.pageSize,
    orderBy: convertToApiColumn(search.sortColumn),
    orderDirection: search.sortOrder as SortOrder,
  };

  const { data, isPending } = useCertificationsQuery(apiParams);

  // APIデータをPersonCell期待形式に変換
  const transformedData = useMemo(() => {
    return (data?.items ?? []).map((item) => ({
      ...item,
      certificationHolder: {
        id: item.certificationHolder.personId || "",
        name: item.certificationHolder.name || "",
        namePronunciation: item.certificationHolder.namePronunciation || "",
        avatarUrl: item.certificationHolder.avatarUrl || undefined,
      },
    })) as TransformedCertificationsTableRow[];
  }, [data?.items]);

  const table = useReactTable({
    data: transformedData,
    pageCount: data?.maxPage,
    columns: columns,
    state: { pagination, sorting },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    manualPagination: true,
    manualSorting: true,
  });

  const displayState = useTableDisplayState(isPending, data?.items ?? []);

  return { table, displayState };
};
